// The order list's rules, lifted from OrderListScreen (Oct 2026), which keeps the requests,
// the dialogs' state and their inputs around them: the tab the list opens on, the actions an
// order's menu offers and the total its row shows, the ship dialog's film defaults, packaging
// rows, free stock, quantity inputs and request body, and the refund dialog's submit gate.
import { usedFilmQuantity } from './formInputs';
import { hasRefundableItem } from './refundForm';
import { freeStock } from './stockAdjustments';

export const orderListFilter = (filterParam, pendingOrders) => {
  const defaultFilter = pendingOrders > 0 ? 'pending' : 'all';
  return filterParam || defaultFilter;
}; // the tab the list shows: the URL's filter, else the pending tab while pending orders exist (their count loads after the first render, so a list opened without a filter fetches all, then pending: KNOWN_BUGS.md, the order list's double fetch)

export const orderActions = (order) => {
  // Check if all order items are finished:
  const allFinished = order.orderItems.every(
    (item) => item.status === 'finished'
  );

  const notRefunded = !order.isRefunded; // once spelled `order?.isRefunded === false` for the refund and `!order.isRefunded` for the cancellation, which agree for every listed order (GET /api/orders sends hydrated documents, whose isRefunded defaults to false); one spelling since Oct 2026, user decision

  const showRefund = order.status !== 'cancelled' && notRefunded; // only allow to refund, if order has not been cancelled or refunded yet

  const showCancelOrder =
    (order.status === 'pending' || order.status === 'processing') &&
    notRefunded; // only allow to cancel an order, if the order has not been shipped and not refunded yet

  const showDangerSection = showRefund || showCancelOrder;

  return {
    showProcessOrder: order.status === 'pending', // starts the processing (PUT status 'processing', then the process-order page)
    showContinueProcessing: order.status === 'processing' && !allFinished, // opens the process-order page (the screen walked the items a second time for this, someNotFinished, the negation of allFinished; once since Oct 2026, user decision)
    showOrderShipped: order.status === 'processing' && allFinished, // opens the ship dialog
    showOrderDelivered: order.status === 'shipped',
    showRefund,
    showCancelOrder,
    showDangerSection,
  };
}; // which items an order's menu offers besides "View details"

export const filmInputDefaults = (rows) => {
  const defaults = {};
  for (const r of rows) defaults[r.orderItemId] = r.reservedQty; // compute the default "used transfer film" inputs, which are going to be used inside the modal that asks the admin to enter the used quantities
  return defaults;
}; // the ship dialog's used-film inputs as it opens: each pooled item's reserved count (the rows of GET /api/orders/:id/film-usage)

export const missingCardboardItems = (packagingGapItems) =>
  packagingGapItems.filter((it) => it.missingCardboard); // the order items whose layout references no cardboard box (the dialog asks for the boxes used)

export const missingFoamItems = (packagingGapItems) =>
  packagingGapItems.filter((it) => it.missingFoamLid || it.missingFoamBase); // the order items whose layout references no foam lid or base (the dialog asks for the foams used)

export const selectedFreeStock = (options, id) => {
  const opt = options.find((b) => String(b._id) === String(id)); // get the current shipping box
  return opt ? freeStock(opt) : 0; // calculate the free stock of the current shipping box
}; // the free stock of the option a shipping box or keycap puller row selected, 0 while it selected none

export const packagingQuantityFromInput = (value, free) =>
  Math.min(free, usedFilmQuantity(value)); // a shipping box or keycap puller row's quantity: a whole number from 0 to the free stock, 0 for anything but a number (the used film's rule; until Oct 2026 a decimal stayed in the field and the submit dropped the row without a message, fixed on the spot)

export const extraPackagingQuantityFromInput = (value) =>
  usedFilmQuantity(value); // an extra cardboard box or foam row's quantity: a whole number of 0 or more, the used film's rule (no cap here: the status route refuses more than the free stock; until Oct 2026 a negative stayed in the field and the submit dropped the row without a message, fixed on the spot)

export const usedFilmFromInput = (value, reservedQty) =>
  Math.min(reservedQty, usedFilmQuantity(value)); // a pooled item's used transfer film: a whole number from 0 to what the order reserved (the route refuses more, as film used beyond the reservation is scrap to book by hand; until Oct 2026 the field took any count and the route refused the whole shipment, fixed on the spot); capped without a message like the box and puller rows: the row's label shows the reservation and the dialog's text above the rows says scrap is booked by hand

const sanitize = (arr) =>
  arr // the dialog's row lists are arrays from the start, so the `(arr || [])` went (Oct 2026, user decision)
    .filter((r) => r.id && Number(r.qty) > 0)
    .map((r) => ({ id: String(r.id), qty: Number(r.qty) })); // basic client-side sanitization: strip empty ids / zero qty (the whole-number check went with the Oct 2026 fix: a row's quantity comes through the two readers above, which floor it)

export const shipmentAdjustments = ({
  filmRows,
  filmInputs,
  manualShipBoxes,
  manualPullers,
  extraCardboardRows,
  extraFoamRows,
}) => {
  // build the payload for used transferFilm:
  const usedFilmsByOrderItem = {};
  for (const r of filmRows) {
    usedFilmsByOrderItem[r.orderItemId] = filmInputs[r.orderItemId]; // the field's own number: its change handler stores a whole count and the dialog's defaults are the route's reserved counts, whole numbers from the layout editor's mold-run count, so the second pass through usedFilmQuantity went (Oct 2026, user decision; a layout with a decimal film quantity, which the editor never writes, would get the route's 400 instead of a floored count)
  }

  // build the payload for shippingBoxes, keycapPullers, extra cardboardBoxes and extra foams:
  const manualAdjustments = {
    shippingBoxes: sanitize(manualShipBoxes),
    keycapPullers: sanitize(manualPullers),
    extraCardboardBoxes: sanitize(extraCardboardRows),
    extraFoams: sanitize(extraFoamRows),
  };

  return { usedFilmsByOrderItem, manualAdjustments };
}; // what the ship dialog's Confirm sends beside the tracking number and provider: the used film per pooled item and the packaging rows worth sending

export const orderStatusBody = (newStatus, shipment) => ({
  status: newStatus,
  ...shipment,
}); // the body of PUT /api/orders/status/:id: the status alone for processing, delivered and cancelled, with 'shipped' the dialog's tracking number, provider, used film and packaging rows (the route reads them for 'shipped' only; master's handler sent them empty for the other transitions, dropped Oct 2026, user decision)

export const refundExceedsOrderTotal = (refundAmount, maxRefund) =>
  refundAmount > maxRefund; // make sure the total refund amount does not exceed the order.totalGross: reachable today through the refund type switch, which keeps the typed value, so 100 % of a 49.90 € item becomes 100 € per piece (KNOWN_BUGS.md, the refund modal's inputs); the typed value, the quantity and the whole shipping are capped, so once that bug is fixed the lines add up to the order total at most and this cap stays as a safety net beside the backend's own (user decision Oct 2026)

export const canSubmitRefund = ({
  refundOrderId,
  refundAmount,
  maxRefund,
  refundItems,
  orderItemsOfRefundOrder,
}) => {
  if (!refundOrderId) return false;
  if (refundAmount <= 0) return false; // the refundAmount must be greater than 0
  if (refundExceedsOrderTotal(refundAmount, maxRefund)) return false; // the total refund must not exceed the order total

  return hasRefundableItem(refundItems, orderItemsOfRefundOrder); // check if at least one refund item is configured with a refund value > 0
}; // a quick check, if a refund can be submitted
